import { useEffect, useMemo, useRef, useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Input } from '@/components/ui/input';
import { Check, Plus } from '@/icons/Icon';
import { cn } from '@/lib/utils';
import { FIELD_MENU_CLASS } from '@/lib/field-skin';
import {
  filterOutreachPlatformOptions,
  findExactOutreachPlatformMatch,
  isExactOutreachPlatformMatch,
} from './outreach-platform-helpers';

type Props = {
  options: readonly string[];
  value: string;
  onChange: (next: string) => void;
  disabled?: boolean;
  error?: string | null;
  /** DOM id for the input; the listbox/option ids derive from it. */
  id?: string;
  placeholder?: string;
  listLabel?: string;
};

type Row = { kind: 'option'; value: string } | { kind: 'add'; value: string };

/**
 * Creatable single-select combobox — the same free-text input is both the
 * trigger (`PopoverTrigger asChild`) and the search/filter box, unlike
 * `CountryPhoneField`'s separate trigger-button + in-popover search input.
 * That shape is required here because the field's whole value is free text
 * (pick a preset or type a custom platform), not a fixed catalog selection.
 */
export function OutreachPlatformField({
  options,
  value,
  onChange,
  disabled,
  error,
  id = 'outreach-platform',
  placeholder = 'Search or add a platform',
  listLabel = 'Outreach platforms',
}: Props) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState(value);
  const [activeIndex, setActiveIndex] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const filtered = useMemo(
    () => filterOutreachPlatformOptions(options, query),
    [options, query],
  );
  const trimmedQuery = query.trim();
  const showAddRow = trimmedQuery.length > 0 && !isExactOutreachPlatformMatch(options, trimmedQuery);

  const rows: Row[] = useMemo(
    () => [
      ...filtered.map((v): Row => ({ kind: 'option', value: v })),
      ...(showAddRow ? [{ kind: 'add', value: trimmedQuery } as const] : []),
    ],
    [filtered, showAddRow, trimmedQuery],
  );

  useEffect(() => {
    setActiveIndex(0);
  }, [query, open]);

  useEffect(() => {
    if (!open) return;
    const el = listRef.current?.querySelector<HTMLElement>(
      `[data-platform-index="${activeIndex}"]`,
    );
    el?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex, open]);

  function commit(next: string) {
    onChange(next);
    setQuery(next);
    setOpen(false);
  }

  // Row commits (below) bind onMouseDown + preventDefault instead of onClick,
  // so a click on a row never blurs this input first — which would otherwise
  // race this revert and could wipe the typed text before the click handler
  // runs. Any blur/outside-interaction that DOES reach here is therefore a
  // real abandon with no commit (Tab away, Escape, click truly outside), so
  // revert to the last committed value.
  function revertAndClose() {
    setOpen(false);
    setQuery(value);
  }

  function isInsideInput(target: EventTarget | null): boolean {
    return target instanceof Node && !!inputRef.current?.contains(target);
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!open) {
        setOpen(true);
        return;
      }
      setActiveIndex((i) => Math.min(i + 1, Math.max(0, rows.length - 1)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      // Exact match wins over the highlighted row: activeIndex resets to 0
      // on every keystroke, so a partial-match query (e.g. "sale") would
      // otherwise silently commit the top filtered option ("Saleshandy")
      // instead of what was actually typed, whenever the user presses Enter
      // without arrowing first.
      const exactMatch = findExactOutreachPlatformMatch(options, trimmedQuery);
      if (exactMatch) {
        commit(exactMatch);
      } else if (trimmedQuery.length > 0) {
        commit(trimmedQuery);
      } else {
        const activeRow = rows[activeIndex];
        if (activeRow) commit(activeRow.value);
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      revertAndClose();
    }
  }

  return (
    <div className="onboarding-field">
      <Popover
        open={open}
        onOpenChange={(next) => {
          // Only ever OPEN from here. Radix calls this with `false` both for
          // a genuine outside dismiss AND for a second click on the trigger
          // while already open (its Trigger always toggles on click) — the
          // latter must NOT revert the in-progress text, so closing is
          // driven exclusively by the explicit paths below (blur, Escape,
          // PopoverContent's onInteractOutside), never by this callback.
          if (next) setOpen(true);
        }}
      >
        <PopoverTrigger asChild>
          <Input
            ref={inputRef}
            id={id}
            type="text"
            value={query}
            maxLength={100}
            onChange={(e) => {
              setQuery(e.target.value);
              setOpen(true);
            }}
            onBlur={() => {
              if (open) revertAndClose();
            }}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            autoComplete="off"
            role="combobox"
            aria-expanded={open}
            aria-controls={`${id}-listbox`}
            aria-autocomplete="list"
            aria-activedescendant={
              open && rows[activeIndex] ? `${id}-opt-${activeIndex}` : undefined
            }
            disabled={disabled}
            className="warmup-field"
          />
        </PopoverTrigger>
        <PopoverContent
          align="start"
          sideOffset={4}
          className={cn(FIELD_MENU_CLASS, 'w-[var(--radix-popover-trigger-width)] p-1')}
          onOpenAutoFocus={(e) => e.preventDefault()}
          onInteractOutside={(e) => {
            // A click back on the trigger input is reported as "outside"
            // this portaled content too — ignore it (see the onOpenChange
            // comment above); only a genuine outside interaction reverts.
            if (isInsideInput(e.target)) return;
            revertAndClose();
          }}
        >
          <div
            id={`${id}-listbox`}
            role="listbox"
            aria-label={listLabel}
            ref={listRef}
            className="max-h-[280px] overflow-y-auto pb-1"
          >
            {rows.map((row, i) => {
              const isActive = i === activeIndex;
              const isSelected = row.kind === 'option' && row.value === value;
              return (
                <button
                  key={row.kind === 'option' ? row.value : '__add__'}
                  id={`${id}-opt-${i}`}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  data-platform-index={i}
                  onMouseEnter={() => setActiveIndex(i)}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    commit(row.value);
                  }}
                  className={cn(
                    'flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm',
                    row.kind === 'add' && 'text-primary',
                    isActive && 'bg-accent text-accent-foreground',
                  )}
                >
                  {row.kind === 'option' ? (
                    <span className="flex w-4 shrink-0 items-center justify-center">
                      {isSelected ? <Check size={13} /> : null}
                    </span>
                  ) : (
                    <Plus size={13} />
                  )}
                  <span className="truncate">
                    {row.kind === 'add' ? <>Add &ldquo;{row.value}&rdquo;</> : row.value}
                  </span>
                </button>
              );
            })}
          </div>
        </PopoverContent>
      </Popover>
      {error ? (
        <p role="alert" className="text-xs text-destructive">
          {error}
        </p>
      ) : null}
    </div>
  );
}
